import { Component, ElementRef, Inject, inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AttachmentsService } from '../../service/files-and-attachments/attachments.service';
import { AttachmentSheetsService } from '../../service/google-sheets/attachment-sheets.service';
import { environment } from '../../../environments/environment';
import { Attachment } from '../../objects/attachment';
import { firstValueFrom, Observable } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common'; // <-- Import CommonModule

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { UsersService } from '../../service/entity-services/users.service';
import { User } from '../../objects/interface-library';
import { FileUploadService } from '../../service/files-and-attachments/file-upload.service';
import { applyAttachmentTarget } from '../../shared/utils/attachment-target';

@Component({
  selector: 'app-new-attachment',
  templateUrl: './new-attachment.component.html',
  styleUrls: ['./new-attachment.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatInput,
    MatButtonModule
  ]
})
export class NewAttachmentComponent {
  private attachmentService = inject(AttachmentsService);
  private attachmentSheetsService = inject(AttachmentSheetsService);
  public usersService = inject(UsersService);
  private fileUploadService: FileUploadService = inject(FileUploadService);
  private attachmentsService: AttachmentsService = inject(AttachmentsService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('fileInput') fileInput!: ElementRef;

  selectedFile: File | null = null;
  isUploading = false;
  uploadComplete = false;
  errorMessage: string | null = null;
  s3Filename: string | null = null;
  isDragOver = false;

  allAttachmentTypes: any;
  primaryDocument = false;
  isAssessmentList = false;
  okToLeaveIntakeForm = false;
  s3BucketLink = environment.s3BucketLink + '/';
  s3Location = environment.s3Location;
  currentAttachment: Attachment = {};
  submitted = false;
  reviewer = true;
  saveContractAvailable = true;
  filePath: any;
  mainContractFile = false;
  message = null;
  title = "New Attachment";
  attachmentTypeReadOnly = false;

  allUsers: User[] = [];
  public allUsers$!: Observable<any[]>;
  public filteredUsers: any[] = [];

  parentEntity: any;

  constructor(public dialogRef: MatDialogRef<NewAttachmentComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any) {

      this.usersService.refreshAllUsersRecords();
      this.allUsers$ = this.usersService.allUsers$;

      console.log('*** NewAttachmentComponent constructor - data:', data);

      if (data && data.entityObject) {
        this.parentEntity = data.entityObject;
        console.log('*** Parent Entity Data:', this.parentEntity);
      }

      // If we're editing an existing attachment, load its data
      if (data && data.attachmentData) {
        console.log('*** Editing existing attachment:', data.attachmentData);
        // Handle both wrapped and unwrapped formats
        const attachmentData = data.attachmentData.attachmentData || data.attachmentData;
        this.currentAttachment = { ...attachmentData };
        this.uploadComplete = true; // Mark as uploaded since file already exists
        console.log('*** Loaded attachment for editing:', this.currentAttachment);
      }
  }

  async ngOnInit() {
    if (!this.currentAttachment.attachmentNum) {
      const datetimestamp = (new Date()).getTime().toString();
      this.currentAttachment.attachmentNum = datetimestamp;
    }

    // Subscribe to the users observable to populate the allUsers array.
    // This is the key fix for the autocomplete.
    this.allUsers$.subscribe({
      next: (users) => {
        this.allUsers = users;
        this.filteredUsers = users; // Initialize the filtered list
      },
      error: (err) => {
        console.error('Failed to get users:', err);
      }
    });

    this.getAttachmentTypes();
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;

    if (fileList && fileList.length > 0) {
      this.selectedFile = fileList[0];
      this.uploadComplete = false; // Reset the flag
      this.errorMessage = null; // Clear any previous errors
      this.uploadFile(); // Automatically start the upload
    }
  }

  /**
   * Handle drag over event for the drop zone
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /**
   * Handle drag leave event for the drop zone
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * Handle drop event for the drop zone
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      this.uploadComplete = false;
      this.errorMessage = null;
      this.uploadFile();
    }
  }

  /**
   * This method handles the file upload process to S3.
   * It is called as soon as a file is selected.
   */
  async uploadFile() {
    if (!this.selectedFile) {
      console.error('No file selected to upload.');
      return;
    }
    
    this.isUploading = true;
    try {
      // 1. Generate a unique, timestamped filename
      const fileExtension = this.selectedFile.name.split('.').pop();
      const uniqueFileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;

      // 2. Get the pre-signed URL from your REST API
      const preSignedUrlResponse = await firstValueFrom(
        this.fileUploadService.getPresignedUrl(uniqueFileName)
      );
      const preSignedUrl = preSignedUrlResponse.presignedUrl;
      
      // 3. Upload the file to S3 using the pre-signed URL.
      await firstValueFrom(
        this.fileUploadService.uploadFileToS3(preSignedUrl, this.selectedFile)
      );

      // 4. Populate the Attachment entity with all the metadata
      this.currentAttachment.s3filename = uniqueFileName;
      this.currentAttachment.directLink = `${preSignedUrl.split('?')[0]}`;
      this.currentAttachment.fileSize = this.selectedFile.size;
      this.currentAttachment.originalFileName = this.selectedFile.name;
      this.currentAttachment.createdDate = (new Date()).toISOString();
      this.uploadComplete = true;

    } catch (error) {
      console.error('An error occurred during file upload:', error);
      this.errorMessage = 'Failed to upload file. Please try again.';
      this.uploadComplete = false;
    } finally {
      this.isUploading = false;
      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  /**
   * This method is called by the "Save Attachment" button.
   * It is only responsible for saving the attachment's metadata to the database.
   */
  async onSaveAttachment() {
    if (!this.uploadComplete) {
      this.errorMessage = 'Please wait for the file upload to complete.';
      return;
    }

    // If we're editing an existing attachment, it already has associatedEntityId and associatedEntityType
    // Only determine entity info if we're creating a new attachment
    if (!this.currentAttachment.associatedEntityId || !this.currentAttachment.associatedEntityType) {
      // DON'T ORPHAN AN ATTACHMENT.  BAIL IF YOU DON'T KNOW WHAT ENTITY IT BELONGS TO.
      // applyAttachmentTarget() returns false for both an unrecognized entityType
      // and a recognized one whose id field is empty — the same two bail-outs this
      // method used to spell out inline. Shared with AttachDocumentDialogComponent
      // so both writers resolve the parent id identically.
      if (!applyAttachmentTarget(this.currentAttachment, this.parentEntity)) {
        console.error('Cannot attach to this entity:', this.parentEntity);
        this.errorMessage = 'The attachment could not be associated with a valid entity. Please ensure the entity has an ID.';
        this.isUploading = false; // Stop the loading spinner
        return;
      }
    }

    this.isUploading = true;

    // console.log('*** this is your attachment object: ', this.currentAttachment);

    try {
      const savedAttachment = await firstValueFrom(
        this.attachmentsService.saveAttachment(this.currentAttachment)
      );

      // Stop the spinner and manually trigger change detection
      this.isUploading = false;
      this.cdr.detectChanges();

      // Small delay to ensure UI updates before closing
      setTimeout(() => {
        this.dialogRef.close(this.currentAttachment);
      }, 50);

    } catch (error) {
      console.error('An error occurred while saving attachment metadata:', error);
      this.errorMessage = 'Failed to save attachment metadata. Please try again.';
      this.isUploading = false;
      this.cdr.detectChanges();
    }
  }

  filterUsers(searchText: string | null): void {
    // Check if searchText is null, undefined, or not a string.
    // If so, display all users and exit.
    if (typeof searchText !== 'string' || searchText === null) {
      this.filteredUsers = this.allUsers;
      return;
    }

    const filterValue = searchText.toLowerCase();
    this.filteredUsers = this.allUsers.filter(user =>
      user.friendlyName.toLowerCase().includes(filterValue)
    );
  }

  displayFn(user: User): string {
    return user ? user.friendlyName : '';
  }

  clearSelection(): void {
    this.selectedFile = null;
    this.uploadComplete = false;
    this.s3Filename = null;
    this.errorMessage = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Fetches the attachment types from the service.
   *
   * The mat-select for `allAttachmentTypes` will now be populated.
   */
  getAttachmentTypes(): void {
    this.attachmentSheetsService.getAttachmentTypes().subscribe({
      next: (types) => {
        console.log('*** Attachment types received:', types);
        console.log('*** Is array?', Array.isArray(types));
        console.log('*** Length:', Array.isArray(types) ? types.length : 'N/A');

        // Use the array directly, don't extract [0]
        this.allAttachmentTypes = Array.isArray(types) ? types : [];

        console.log('*** Loaded attachment types:', this.allAttachmentTypes.length);
      },
      error: (err) => {
        console.error('Failed to get attachment types', err);
        this.allAttachmentTypes = [];
      }
    });
  }

  onCancel() {
    this.dialogRef.close(null);
  }
}